import {
  addDoc,
  collection,
  getDocs,
  doc,
  query,
  where,
  orderBy,
  getDoc,
  updateDoc,
  deleteDoc,
  deleteField,
  serverTimestamp,
  Timestamp,
  documentId,
  arrayUnion,
  arrayRemove,
} from 'firebase/firestore';
import { ref, deleteObject } from 'firebase/storage';
import { createCallable } from '../firebaseFunctions';
import { db, storage } from '../firebase';
import { authService } from './authService';
import { orgStampFor } from './orgStamp';
import { timestampMillis } from '../utils/timestampMillis';
import {
  projectAccessLevel,
  canEditAtLevel,
  canCreateProjects,
  seesOnlyAssignedProjects,
} from '@shared-lib/org-access';

export const projectService = {
  async getProjects() {
    try {
      const user = authService.getCurrentUser();
      if (!user) {
        throw new Error('User not authenticated');
      }

      const projectsRef = collection(db, 'projects');
      const q = query(
        projectsRef,
        where('ownerId', '==', user.uid),
        orderBy('createdAt', 'desc')
      );

      const snapshot = await getDocs(q);
      const projects = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      return projects;
    } catch (error) {
      console.error('Error fetching projects:', error);
      throw error;
    }
  },

  async getProject(projectId) {
    try {
      const projectRef = doc(db, 'projects', projectId);
      const projectSnap = await getDoc(projectRef);

      if (projectSnap.exists()) {
        return { id: projectSnap.id, ...projectSnap.data() };
      } else {
        throw new Error(`Project ${projectId} not found`);
      }
    } catch (error) {
      console.error('Error fetching project:', error);
      throw error;
    }
  },

  /**
   * Create a project owned by the signed-in user, stamped with their
   * organization (`orgId`) when they have one so the books' members can
   * open it. Returns the new document reference.
   */
  async createProject(fields) {
    const user = authService.getCurrentUser();
    if (!user) throw new Error('User not authenticated');
    return addDoc(collection(db, 'projects'), {
      ...fields,
      ...(await orgStampFor(user.uid)),
      ownerId: user.uid,
      createdAt: serverTimestamp(),
      lastModified: serverTimestamp(),
      // iOS decodes projects into a model that reads `updatedAt`; a project
      // written without it used to be dropped from the iOS project list,
      // making imported floor plans unopenable there.
      updatedAt: serverTimestamp(),
    });
  },

  async updateProjectAddress(projectId, addressData) {
    try {
      const user = authService.getCurrentUser();
      if (!user) {
        throw new Error('User not authenticated');
      }

      const projectRef = doc(db, 'projects', projectId);

      const updateData = {
        address: {
          streetAddress: addressData.streetAddress,
          city: addressData.city || 'Los Angeles',
          state: addressData.state || 'CA',
          zipCode: addressData.zipCode,
          fullAddress:
            addressData.fullAddress ||
            `${addressData.streetAddress}, ${addressData.city || 'Los Angeles'}, ${addressData.state || 'CA'} ${addressData.zipCode || ''}`.trim(),
          coordinates: addressData.coordinates || null,
        },
        lastModified: serverTimestamp(),
        modifiedBy: user.uid,
      };

      // Add property data if available
      if (addressData.propertyData) {
        updateData.propertyData = {
          lastUpdated: new Date().toISOString(),
        };

        // Only add fields that have values (Firebase doesn't accept undefined)
        if (addressData.propertyData.apn !== undefined) {
          updateData.propertyData.apn = addressData.propertyData.apn;
        }
        if (addressData.propertyData.useType !== undefined) {
          updateData.propertyData.useType = addressData.propertyData.useType;
        }
        if (
          addressData.propertyData.yearBuilt !== undefined &&
          addressData.propertyData.yearBuilt !== null
        ) {
          updateData.propertyData.yearBuilt =
            addressData.propertyData.yearBuilt;
        }
        if (
          addressData.propertyData.squareFeet !== undefined &&
          addressData.propertyData.squareFeet !== null
        ) {
          updateData.propertyData.squareFeet =
            addressData.propertyData.squareFeet;
        }
        if (
          addressData.propertyData.units !== undefined &&
          addressData.propertyData.units !== null
        ) {
          updateData.propertyData.units = addressData.propertyData.units;
        }
        if (
          addressData.propertyData.landValue !== undefined &&
          addressData.propertyData.landValue !== null
        ) {
          updateData.propertyData.landValue =
            addressData.propertyData.landValue;
        }
        if (
          addressData.propertyData.improvementValue !== undefined &&
          addressData.propertyData.improvementValue !== null
        ) {
          updateData.propertyData.improvementValue =
            addressData.propertyData.improvementValue;
        }
        if (
          addressData.propertyData.totalValue !== undefined &&
          addressData.propertyData.totalValue !== null
        ) {
          updateData.propertyData.totalValue =
            addressData.propertyData.totalValue;
        }
      }

      // Add parcel boundary if available
      if (addressData.parcelBoundary) {
        updateData.parcelBoundary = addressData.parcelBoundary;
      }

      await updateDoc(projectRef, updateData);

      return {
        success: true,
        projectId,
        address: updateData.address,
        propertyData: updateData.propertyData || null,
      };
    } catch (error) {
      console.error('Error updating project address:', error);
      throw error;
    }
  },

  async updateProject(projectId, updateData) {
    try {
      const user = authService.getCurrentUser();
      if (!user) {
        throw new Error('User not authenticated');
      }

      const projectRef = doc(db, 'projects', projectId);

      // Add metadata
      const dataWithMetadata = {
        ...updateData,
        lastModified: serverTimestamp(),
        modifiedBy: user.uid,
      };

      await updateDoc(projectRef, dataWithMetadata);

      return {
        success: true,
        projectId,
      };
    } catch (error) {
      console.error('Error updating project:', error);
      throw error;
    }
  },

  async deleteProject(projectId) {
    try {
      const user = authService.getCurrentUser();
      if (!user) {
        throw new Error('User not authenticated');
      }

      // Verify the user owns this project
      const projectRef = doc(db, 'projects', projectId);
      const projectSnap = await getDoc(projectRef);

      if (!projectSnap.exists()) {
        throw new Error('Project not found');
      }

      const projectData = projectSnap.data();
      if (projectData.ownerId !== user.uid) {
        throw new Error('You do not have permission to delete this project');
      }

      // Delete the project
      await deleteDoc(projectRef);

      return {
        success: true,
        projectId,
      };
    } catch (error) {
      console.error('Error deleting project:', error);
      throw error;
    }
  },

  async getAllProjects() {
    try {
      const user = authService.getCurrentUser();
      if (!user) {
        throw new Error('User not authenticated');
      }

      const projectsRef = collection(db, 'projects');
      const q = query(projectsRef, orderBy('createdAt', 'desc'));

      const snapshot = await getDocs(q);
      const allProjects = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Get unique owner IDs from other projects
      const ownerIds = [
        ...new Set(allProjects.map((p) => p.ownerId).filter((id) => id)),
      ];

      // Fetch owner information if there are any owners
      const ownerMap = {};
      if (ownerIds.length > 0) {
        // Firestore has a limit of 10 for 'in' queries, so we need to batch
        const batchSize = 10;
        for (let i = 0; i < ownerIds.length; i += batchSize) {
          const batch = ownerIds.slice(i, i + batchSize);
          const usersRef = collection(db, 'users');
          const usersQuery = query(usersRef, where(documentId(), 'in', batch));
          const usersSnapshot = await getDocs(usersQuery);

          usersSnapshot.docs.forEach((doc) => {
            const userData = doc.data();
            ownerMap[doc.id] =
              userData.email || userData.displayName || 'Unknown User';
          });
        }
      }

      // Add owner information to projects
      const projectsWithOwners = allProjects.map((project) => ({
        ...project,
        ownerEmail: ownerMap[project.ownerId] || 'Unknown User',
      }));

      // Separate user's projects and others' projects
      const userProjects = projectsWithOwners.filter(
        (p) => p.ownerId === user.uid
      );
      const otherProjects = projectsWithOwners.filter(
        (p) => p.ownerId !== user.uid
      );

      return {
        userProjects,
        otherProjects,
        allProjects: projectsWithOwners,
      };
    } catch (error) {
      console.error('Error fetching all projects:', error);
      throw error;
    }
  },

  // Project Sharing Functions

  /**
   * Find a user by email (uses Cloud Function)
   */
  async findUserByEmail(email) {
    const findUser = createCallable('findUserByEmail');
    const result = await findUser({ email });
    return result.data;
  },

  /**
   * Add an existing account to a project as a viewer.
   *
   * Goes through the `addProjectViewer` callable rather than writing
   * `viewerIds` here: the same operation has to grant the viewer app access
   * and email them, and Firestore rules let a client write only its own user
   * doc. Returns `{ success: false, canInvite: true }` when no account has
   * that email, so the caller can offer an invitation instead, and `notified`
   * saying whether the email went out — the share stands either way. Throws
   * `already-exists` when they are a viewer already.
   */
  async addProjectViewerByEmail(projectId, email) {
    try {
      const addViewer = createCallable('addProjectViewer');
      const result = await addViewer({ projectId, email });
      return result.data;
    } catch (error) {
      console.error('Error adding viewer:', error);
      throw error;
    }
  },

  /**
   * Remove a viewer from a project
   */
  async removeProjectViewer(projectId, viewerUid) {
    try {
      const projectRef = doc(db, 'projects', projectId);
      await updateDoc(projectRef, {
        viewerIds: arrayRemove(viewerUid),
      });
    } catch (error) {
      console.error('Error removing viewer:', error);
      throw error;
    }
  },

  /**
   * Copy a shared project (uses Cloud Function)
   */
  async copySharedProject(projectId) {
    const copyProject = createCallable('copySharedProject');
    const result = await copyProject({ projectId });
    return result.data;
  },

  /**
   * Get project collaborators (uses Cloud Function)
   */
  async getProjectCollaborators(projectId) {
    const getCollaborators = createCallable('getProjectCollaborators');
    const result = await getCollaborators({ projectId });
    return result.data;
  },

  /**
   * Check user's access level for a project
   */
  getUserAccessLevel(project, userId, claims) {
    // 'owner' | 'editor' | 'viewer' | 'none'. `claims` are the signed-in
    // user's ID-token claims (`authService.getTokenClaims()`); without them
    // an organization member reads as 'none', as the rules would treat a
    // token that carries no organization.
    return projectAccessLevel(project, userId, claims);
  },

  /**
   * Send project invitation to a non-existing user
   */
  async sendProjectInvitation(projectId, inviteeEmail) {
    try {
      const sendInvitation = createCallable('sendProjectInvitation');
      const result = await sendInvitation({ projectId, inviteeEmail });
      return result.data;
    } catch (error) {
      console.error('Error sending invitation:', error);
      throw error;
    }
  },

  /**
   * Accept a project invitation (called during signup)
   */
  async acceptProjectInvitation(invitationToken) {
    try {
      const acceptInvitation = createCallable('acceptProjectInvitation');
      const result = await acceptInvitation({ invitationToken });
      return result.data;
    } catch (error) {
      console.error('Error accepting invitation:', error);
      throw error;
    }
  },

  /**
   * Cancel a pending project invitation
   */
  async cancelProjectInvitation(invitationId) {
    try {
      const cancelInvitation = createCallable('cancelProjectInvitation');
      const result = await cancelInvitation({ invitationId });
      return result.data;
    } catch (error) {
      console.error('Error cancelling invitation:', error);
      throw error;
    }
  },

  /**
   * Find the project that contains a specific structure
   * This is important for security - to determine access rights from structure ID
   */
  async findProjectByStructureId(structureId) {
    try {
      // Find project for structure

      // First, get the structure to find its projectId
      const structureRef = doc(db, 'structures', structureId);
      const structureSnap = await getDoc(structureRef);

      if (!structureSnap.exists()) {
        return null;
      }

      const structureData = structureSnap.data();
      const projectId = structureData.projectId;

      // Get project ID from structure

      if (!projectId) {
        return null;
      }

      // Fetch the project
      const project = await this.getProject(projectId);
      return project;
    } catch (error) {
      console.error('Error finding project by structure ID:', error);
      return null;
    }
  },

  // ---------------------------------------------------------------------------
  // Gallery / cover photo
  //
  // Mirrors the iOS-side `ProjectService` methods (see
  // `Housfully/Housfully/Services/ProjectService.swift`):
  //   - addPhotosToGallery(_:photoUrls:)
  //   - setProjectCoverPhoto(_:photoUrl:)
  //   - removeProjectCoverPhoto(_:)
  //
  // Photo objects on the project doc are `{ url, createdAt }`. iOS uses
  // `Timestamp()` (a Firestore Timestamp); we match that here so iOS and web
  // serialize identically and the cover-photo resolver
  // (utils/projectCoverPhoto.js) sees consistent timestamps.
  // ---------------------------------------------------------------------------

  /**
   * Set the project's cover photo URL.
   * Mirrors iOS `ProjectService.setProjectCoverPhoto`.
   */
  async setProjectCoverPhoto(projectId, photoUrl) {
    const projectRef = doc(db, 'projects', projectId);
    await updateDoc(projectRef, {
      imageUrl: photoUrl,
      updatedAt: serverTimestamp(),
    });
  },

  /**
   * Clear the project's cover photo (removes `imageUrl` from the doc).
   * Mirrors iOS `ProjectService.removeProjectCoverPhoto`.
   */
  async removeProjectCoverPhoto(projectId) {
    const projectRef = doc(db, 'projects', projectId);
    await updateDoc(projectRef, {
      imageUrl: deleteField(),
      updatedAt: serverTimestamp(),
    });
  },

  /**
   * Append a photo to `project.galleryPhotos`.
   * Mirrors iOS `ProjectService.addPhotosToGallery` for the single-photo case.
   *
   * @param {string} projectId
   * @param {{ url: string, createdAt?: Timestamp }} photo
   */
  async addProjectGalleryPhoto(projectId, photo) {
    if (!photo || typeof photo.url !== 'string' || photo.url.length === 0) {
      throw new Error('addProjectGalleryPhoto: photo.url is required');
    }
    const entry = {
      url: photo.url,
      createdAt: photo.createdAt || Timestamp.now(),
    };
    const projectRef = doc(db, 'projects', projectId);
    await updateDoc(projectRef, {
      galleryPhotos: arrayUnion(entry),
      updatedAt: serverTimestamp(),
    });
    return entry;
  },

  /**
   * Remove a photo from `project.galleryPhotos` and delete its storage objects
   * (full-size + `_400.jpg` thumbnail). If the removed photo was the cover
   * (`project.imageUrl` matched), the cover is updated to the next most-recent
   * gallery photo, or cleared if none remain.
   *
   * iOS does not implement delete today — this is web-only. The storage path
   * and `_400.jpg` thumbnail convention is set by iOS in
   * `PhotoGalleryViewController.uploadSinglePhoto`.
   *
   * @param {string} projectId
   * @param {string} photoUrl Full download URL of the photo to delete.
   */
  async deleteProjectGalleryPhoto(projectId, photoUrl) {
    if (typeof photoUrl !== 'string' || photoUrl.length === 0) {
      throw new Error('deleteProjectGalleryPhoto: photoUrl is required');
    }

    const projectRef = doc(db, 'projects', projectId);
    const projectSnap = await getDoc(projectRef);
    if (!projectSnap.exists()) {
      throw new Error(`Project ${projectId} not found`);
    }
    const projectData = projectSnap.data();
    const gallery = Array.isArray(projectData.galleryPhotos)
      ? projectData.galleryPhotos
      : [];

    // Find the exact photo object so arrayRemove matches (Firestore matches
    // by deep equality on array elements).
    const entry = gallery.find((p) => p && p.url === photoUrl);

    // Build the update. If this was the cover photo, also update imageUrl.
    const update = {
      updatedAt: serverTimestamp(),
    };
    if (entry) {
      update.galleryPhotos = arrayRemove(entry);
    }
    if (projectData.imageUrl === photoUrl) {
      const remaining = gallery.filter((p) => p && p.url !== photoUrl);
      const nextCover = _pickMostRecentPhotoUrl(remaining);
      update.imageUrl = nextCover || deleteField();
    }

    await updateDoc(projectRef, update);

    // Best-effort delete the storage objects. We tolerate failures (e.g.
    // already-deleted, no permission) so the Firestore write isn't blocked.
    const storagePath = _parseStoragePathFromDownloadUrl(photoUrl);
    if (storagePath) {
      const fullRef = ref(storage, storagePath);
      deleteObject(fullRef).catch(() => {});
      const thumbPath = storagePath.replace(/\.jpg$/i, '_400.jpg');
      if (thumbPath !== storagePath) {
        const thumbRef = ref(storage, thumbPath);
        deleteObject(thumbRef).catch(() => {});
      }
    }
  },

  /**
   * Get all projects the user has access to (owned and shared)
   */
  async getAccessibleProjects() {
    try {
      const user = authService.getCurrentUser();
      if (!user) {
        throw new Error('User not authenticated');
      }

      const projectsRef = collection(db, 'projects');
      // The cached token's claims — the ones the rules judge these reads by.
      const claims = await authService.getTokenClaims();

      // Three independent reads, one round trip: the projects the user owns,
      // the ones shared with them, and their organization's. The team list is
      // the only one that can fail on its own (a claim the rules refuse, a
      // token that cannot be read) and it must not take the other two down —
      // a member with a broken claim still owns their projects.
      // the team read failing alone is reported, not only logged: a page
      // that lists the organization's projects says what it could not read
      let teamProjectsError = null;
      const [ownedSnapshot, viewerSnapshot, teamProjects] = await Promise.all([
        getDocs(
          query(
            projectsRef,
            where('ownerId', '==', user.uid),
            orderBy('createdAt', 'desc')
          )
        ),
        getDocs(
          query(
            projectsRef,
            where('viewerIds', 'array-contains', user.uid),
            orderBy('createdAt', 'desc')
          )
        ),
        getTeamProjects(projectsRef, user.uid, claims).catch((error) => {
          console.error('Error fetching team projects:', error);
          teamProjectsError = error;
          return [];
        }),
      ]);

      const ownedProjects = ownedSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        accessLevel: 'owner',
      }));

      const teamIds = new Set(teamProjects.map((p) => p.id));

      // A project both shared with the user and owned by their organization
      // is listed once, under the team, at the level the organization gives.
      const sharedProjects = viewerSnapshot.docs
        .filter((doc) => !teamIds.has(doc.id))
        .map((doc) => ({
          id: doc.id,
          ...doc.data(),
          accessLevel: 'viewer',
        }));

      return {
        ownedProjects,
        sharedProjects,
        teamProjects,
        teamProjectsError,
        allProjects: [...ownedProjects, ...sharedProjects, ...teamProjects],
        // A field member's team list is their assignments; only owner and
        // office members (or anyone outside an organization) create projects.
        assignedOnly: seesOnlyAssignedProjects(claims),
        canCreateProjects: canCreateProjects(claims),
      };
    } catch (error) {
      console.error('Error fetching accessible projects:', error);
      throw error;
    }
  },
};

// ---------------------------------------------------------------------------
// Local helpers (gallery)
// ---------------------------------------------------------------------------

/**
 * The projects of the user's organization that someone else owns, newest
 * first, each with the access level the organization gives the user. Empty
 * when the user's token carries no organization.
 *
 * A field member gets only the projects they are assigned to
 * (`assignedTo` array-contains their uid) — the office sends them to a
 * property; they do not browse every one. The rules do not narrow this (a
 * field member may still READ any of the organization's projects); it is the
 * list, not the permission.
 *
 * Queried on `orgId` (plus the assignment) and sorted here, so it needs no
 * composite index; the rules admit the query because it is constrained to
 * the caller's own `orgId` claim (`inCallerOrg`).
 */
async function getTeamProjects(projectsRef, uid, claims) {
  if (!claims.orgId) return [];
  const constraints = [where('orgId', '==', claims.orgId)];
  if (seesOnlyAssignedProjects(claims)) {
    constraints.push(where('assignedTo', 'array-contains', uid));
  }
  const snapshot = await getDocs(query(projectsRef, ...constraints));
  return snapshot.docs
    .map((doc) => ({ id: doc.id, ...doc.data() }))
    .filter((project) => project.ownerId !== uid)
    .map((project) => ({
      ...project,
      accessLevel: projectAccessLevel(project, uid, claims),
    }))
    .sort((a, b) => _createdAtMillis(b) - _createdAtMillis(a));
}

function _createdAtMillis(item) {
  return timestampMillis(item?.createdAt);
}

function _pickMostRecentPhotoUrl(photos) {
  if (!Array.isArray(photos) || photos.length === 0) return null;
  const sorted = [...photos].sort(
    (a, b) => _createdAtMillis(b) - _createdAtMillis(a)
  );
  for (const p of sorted) {
    if (p && typeof p.url === 'string' && p.url.length > 0) return p.url;
  }
  return null;
}

/**
 * Parse a Firebase Storage download URL into its bucket-relative object path.
 *
 * Firebase download URLs look like:
 *   https://firebasestorage.googleapis.com/v0/b/<bucket>/o/<URL-encoded path>?alt=...
 *
 * Returns `null` if the URL doesn't match that shape (e.g. a non-Firebase URL),
 * in which case the caller should skip the storage delete and only update the
 * Firestore doc.
 */
function _parseStoragePathFromDownloadUrl(downloadUrl) {
  if (typeof downloadUrl !== 'string') return null;
  // Match `/o/<encoded path>` and stop at the query string.
  const match = downloadUrl.match(/\/o\/([^?]+)/);
  if (!match) return null;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return null;
  }
}
